// Notifications Web Push : abonnement de cet appareil au monde (le Conteur peut alors prévenir une personne absente),
// aide à l'installation sur l'écran d'accueil (indispensable sur iPhone) et bandeau d'invitation affiché une seule fois.
// Rien ici ne bloque le jeu : une notification reste un bonus.
import { useEffect, useRef, useState } from "preact/hooks";
import type { PushSubscriptionData } from "../../shared/types";
import { getState, pushToast, send, useAppState } from "../store";
import { isNativeApp, nativePushPermission } from "../native";
import "./NotifyButton.css";

/** Ce que l'appareil permet, du plus bloquant au plus permissif. */
export type EtatNotifications = "installer" | "indisponible" | "invitation" | "refusee" | "active";

/** Issue d'une tentative d'abonnement. Rien n'est affiché ici : l'appelant choisit le message. */
type Issue = "ok" | "non_configure" | "reseau" | "pret" | "echec" | "hors_ligne";

export interface NotifyButtonProps {
  /** « carte » en tête de l'onglet Compagnons ; « menu » dans le menu de l'en-tête. */
  variant?: "carte" | "menu";
}

const DELAI_SERVICE_WORKER_MS = 8_000;
const CLE_BANDEAU = "veillee:notifications-bandeau-vu";

const MSG_NON_CONFIGURE = "Les notifications ne sont pas encore configurées sur ce serveur.";
const MSG_REFUSEES = "Les notifications sont refusées pour Veillée. Tu peux les autoriser dans les réglages de ton appareil.";
const MSG_ANNULEES = "Pas de souci : tu pourras réessayer quand tu voudras.";
const MSG_RESEAU = "Impossible de joindre le serveur pour les notifications. Réessaie dans un instant.";
const MSG_PRET = "Veillée n'est pas encore prête à recevoir des notifications. Recharge la page, puis réessaie.";
const MSG_ECHEC = "Les notifications n'ont pas pu être activées sur cet appareil. Réessaie dans un instant.";
const MSG_HORS_LIGNE = "Connexion perdue : les notifications s'activeront à la reconnexion.";
const MSG_OK = "Parfait ! Le Conteur pourra te prévenir quand il t'appelle.";

const MESSAGE_PAR_ISSUE: Record<Issue, { texte: string; ton: "info" | "succes" | "danger" }> = {
  ok: { texte: MSG_OK, ton: "succes" },
  hors_ligne: { texte: MSG_HORS_LIGNE, ton: "info" },
  non_configure: { texte: MSG_NON_CONFIGURE, ton: "danger" },
  reseau: { texte: MSG_RESEAU, ton: "danger" },
  pret: { texte: MSG_PRET, ton: "danger" },
  echec: { texte: MSG_ECHEC, ton: "danger" },
};

/** Couples monde/personne déjà abonnés pendant ce chargement de page (ou en cours) : un seul envoi par couple. */
const abonnementsEnvoyes = new Set<string>();

// ───────── État de l'appareil ─────────

type NavigateurAutonome = Navigator & { standalone?: boolean };

/** iPhone ou iPad. Un iPad en mode bureau se présente comme un Mac, mais garde un écran tactile. */
function estIOS(): boolean {
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return true;
  return /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
}

/** Lancée depuis l'écran d'accueil (application installée) plutôt que depuis un onglet du navigateur. */
function estInstallee(): boolean {
  const modeAutonome = typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches;
  return modeAutonome || (navigator as NavigateurAutonome).standalone === true;
}

export function etatNotifications(): EtatNotifications {
  // l'appli iPhone n'a ni Web Push ni besoin d'être installée : ses notifications sont celles d'Apple (native.ts)
  if (isNativeApp()) return "indisponible";
  if (estIOS() && !estInstallee()) return "installer";
  if (typeof PushManager === "undefined" || typeof Notification === "undefined" || !("serviceWorker" in navigator)) {
    return "indisponible";
  }
  if (Notification.permission === "granted") return "active";
  if (Notification.permission === "denied") return "refusee";
  return "invitation";
}

/** Clé du monde ouvert et de la personne connectée, pour ne pas envoyer deux fois le même abonnement. */
function cleDuMonde(): string | null {
  const session = getState().session;
  return session ? `${session.code}/${session.playerId}` : null;
}

function journaliser(etape: string, err: unknown): void {
  console.warn(`[notifications] ${etape} :`, err);
}

// ───────── Abonnement ─────────

/** Clé publique VAPID du serveur (base64url), ou null si les notifications ne sont pas configurées. */
async function clePublique(): Promise<string | null> {
  const reponse = await fetch("/api/vapid");
  if (!reponse.ok) throw new Error(`clé VAPID : HTTP ${reponse.status}`);
  const donnees: unknown = await reponse.json();
  const cle = typeof donnees === "object" && donnees !== null && "publicKey" in donnees ? donnees.publicKey : null;
  return typeof cle === "string" && cle.length > 0 ? cle : null;
}

/** base64url → octets, tels qu'attendus par PushManager (applicationServerKey). */
function enOctets(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const binaire = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
  const octets = new Uint8Array(new ArrayBuffer(binaire.length));
  for (let i = 0; i < binaire.length; i++) octets[i] = binaire.charCodeAt(i);
  return octets;
}

/** Le service worker de Veillée, prêt à s'abonner. Échoue au bout de quelques secondes si rien ne répond. */
async function serviceWorkerPret(): Promise<ServiceWorkerRegistration> {
  let minuteur: ReturnType<typeof setTimeout> | undefined;
  const delai = new Promise<never>((_, rejeter) => {
    minuteur = setTimeout(() => rejeter(new Error("service worker absent")), DELAI_SERVICE_WORKER_MS);
  });
  try {
    return await Promise.race([navigator.serviceWorker.ready, delai]);
  } finally {
    clearTimeout(minuteur);
  }
}

function memeCle(abonnement: PushSubscription, octets: Uint8Array<ArrayBuffer>): boolean {
  const actuelle = abonnement.options?.applicationServerKey;
  if (!actuelle) return false;
  const existante = new Uint8Array(actuelle);
  return existante.length === octets.length && existante.every((valeur, i) => valeur === octets[i]);
}

/** L'abonnement existant s'il utilise la bonne clé, sinon un nouveau (l'ancien est résilié si la clé du serveur a changé). */
async function abonner(registration: ServiceWorkerRegistration, cle: string): Promise<PushSubscription> {
  const octets = enOctets(cle);
  const existant = await registration.pushManager.getSubscription();
  if (existant && memeCle(existant, octets)) return existant;
  if (existant) await existant.unsubscribe();
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: octets });
}

/** Envoie l'abonnement au serveur du monde. Faux si la connexion n'est pas ouverte : on réessaiera à la reconnexion. */
function envoyer(abonnement: PushSubscription): boolean {
  const json = abonnement.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) throw new Error("abonnement incomplet");
  if (getState().connection !== "connecte") return false;
  const subscription: PushSubscriptionData = { endpoint, keys: { p256dh, auth } };
  send({ t: "push_subscribe", subscription });
  return true;
}

/** Abonne l'appareil et envoie l'abonnement au monde. Ne montre rien : l'appelant choisit le message. Ne lève jamais. */
async function souscrire(cle: string): Promise<Issue> {
  let cleServeur: string | null;
  try {
    cleServeur = await clePublique();
  } catch (err) {
    journaliser("clé publique", err);
    return "reseau";
  }
  if (cleServeur === null) return "non_configure";

  let registration: ServiceWorkerRegistration;
  try {
    registration = await serviceWorkerPret();
  } catch (err) {
    journaliser("service worker", err);
    return "pret";
  }

  try {
    const abonnement = await abonner(registration, cleServeur);
    if (!envoyer(abonnement)) return "hors_ligne";
    abonnementsEnvoyes.add(cle);
    return "ok";
  } catch (err) {
    journaliser("abonnement", err);
    return "echec";
  }
}

/** Déjà autorisé : réabonnement silencieux, une fois par couple monde/personne et par chargement de page. */
async function resynchroniser(cle: string): Promise<void> {
  if (abonnementsEnvoyes.has(cle)) return;
  abonnementsEnvoyes.add(cle);
  const issue = await souscrire(cle);
  if (issue !== "ok") abonnementsEnvoyes.delete(cle);
}

/**
 * Demande l'autorisation puis abonne l'appareil, avec un message pour chaque issue.
 * À appeler directement depuis un geste de la personne : iOS refuse la demande sinon.
 */
export async function activerNotifications(): Promise<void> {
  if (Notification.permission === "denied") {
    pushToast(MSG_REFUSEES, "danger");
    return;
  }
  // Aucun await avant cette demande : iOS ne l'accepte que pendant le geste de la personne.
  let reponse: NotificationPermission;
  try {
    reponse = await Notification.requestPermission();
  } catch (err) {
    journaliser("permission", err);
    pushToast(MSG_ECHEC, "danger");
    return;
  }
  if (reponse === "denied") {
    pushToast(MSG_REFUSEES, "danger");
    return;
  }
  if (reponse !== "granted") {
    pushToast(MSG_ANNULEES, "info");
    return;
  }
  const cle = cleDuMonde();
  if (cle === null) {
    pushToast(MSG_ECHEC, "danger");
    return;
  }
  const issue = await souscrire(cle);
  const message = MESSAGE_PAR_ISSUE[issue];
  pushToast(message.texte, message.ton);
}

// ───────── Bandeau unique ─────────

/** Repli si le stockage est indisponible : le bandeau ne revient pas pendant ce chargement de page. */
let bandeauAffiche = false;

function bandeauDejaVu(): boolean {
  if (bandeauAffiche) return true;
  try {
    return localStorage.getItem(CLE_BANDEAU) !== null;
  } catch {
    return false;
  }
}

function marquerBandeauVu(): void {
  bandeauAffiche = true;
  try {
    localStorage.setItem(CLE_BANDEAU, "1");
  } catch {
    // Stockage indisponible (navigation privée) : le repli en mémoire suffit.
  }
}

// ───────── Composants ─────────

/**
 * Notifications de l'appareil : aide à l'installation (iPhone, hors écran d'accueil), bouton d'activation,
 * état « Notifications activées » ou refus. Rien à afficher si l'appareil ne sait pas faire de Web Push.
 */
export function NotifyButton(props: NotifyButtonProps) {
  if (isNativeApp()) return <NotifyNative variante={props.variant ?? "carte"} />;
  return <NotifyButtonWeb {...props} />;
}

/** Dans l'appli, iOS pose lui-même la question (native.ts) : il reste à dire ce qui a été répondu. */
function NotifyNative({ variante }: { variante: "carte" | "menu" }) {
  const etat = nativePushPermission();
  if (etat === "refusee") {
    return (
      <p class={`notify notify-refus notify-${variante}`}>
        Les notifications sont refusées pour Veillée. Autorise-les dans Réglages, Notifications, Veillée pour être prévenu quand le Conteur t'appelle.
      </p>
    );
  }
  if (etat === "accordee") {
    return (
      <p class={`notify notify-actif notify-${variante}`}>
        <span aria-hidden="true">🔔</span> Notifications activées
      </p>
    );
  }
  return null;
}

function NotifyButtonWeb(props: NotifyButtonProps) {
  const variante = props.variant ?? "carte";
  const { session, connection } = useAppState();
  const [occupe, setOccupe] = useState(false);
  const [, rafraichir] = useState(0);
  const occupeRef = useRef(false);
  const etat = etatNotifications();
  const cle = session ? `${session.code}/${session.playerId}` : null;

  // Déjà autorisé : on se réabonne en silence dès que la connexion est ouverte (le serveur peut avoir oublié l'abonnement).
  useEffect(() => {
    if (etat !== "active" || connection !== "connecte" || cle === null) return;
    void resynchroniser(cle);
  }, [etat, connection, cle]);

  async function activer(): Promise<void> {
    if (occupeRef.current) return;
    occupeRef.current = true;
    setOccupe(true);
    try {
      await activerNotifications();
    } finally {
      occupeRef.current = false;
      setOccupe(false);
      rafraichir((n) => n + 1);
    }
  }

  if (etat === "indisponible") return null;

  if (etat === "installer") {
    return (
      <section class={`notify notify-aide notify-${variante}`} aria-label="Installer Veillée">
        <span class="notify-aide-icone" aria-hidden="true">📲</span>
        <p class="notify-aide-texte">
          Installe Veillée sur ton écran d'accueil : touche Partager ⬆️ puis « Sur l'écran d'accueil ». C'est indispensable pour recevoir les questions du Conteur. Dans l'appli installée, retrouve ton héros avec ton lien de connexion ou ton code de retour (Rejoindre → « J'ai déjà un héros ici »).
        </p>
      </section>
    );
  }

  if (etat === "refusee") {
    return (
      <p class={`notify notify-refus notify-${variante}`}>
        Les notifications sont refusées pour Veillée. Autorise-les dans les réglages de ton appareil pour être prévenu quand le Conteur t'appelle.
      </p>
    );
  }

  if (etat === "active") {
    return (
      <p class={`notify notify-actif notify-${variante}`}>
        <span aria-hidden="true">🔔</span> Notifications activées
      </p>
    );
  }

  return (
    <div class={`notify notify-${variante}`}>
      <button type="button" class="btn notify-bouton" disabled={occupe} onClick={() => void activer()}>
        {occupe ? "🔔 Activation en cours…" : "🔔 Être prévenu quand le Conteur m'appelle"}
      </button>
    </div>
  );
}

/**
 * Bandeau discret dans le jeu, affiché une seule fois (puis mémorisé) : installer Veillée sur l'iPhone,
 * ou activer les notifications. Refermable.
 */
export function NotifyBanner() {
  if (isNativeApp()) return null;
  return <NotifyBannerWeb />;
}

function NotifyBannerWeb() {
  const etat = etatNotifications();
  const installer = etat === "installer";
  const pertinent = installer || etat === "invitation";
  const [visible, setVisible] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const occupeRef = useRef(false);

  useEffect(() => {
    if (!pertinent || bandeauDejaVu()) return;
    marquerBandeauVu();
    setVisible(true);
  }, [pertinent]);

  async function activer(): Promise<void> {
    if (occupeRef.current) return;
    occupeRef.current = true;
    setOccupe(true);
    try {
      await activerNotifications();
    } finally {
      occupeRef.current = false;
      setOccupe(false);
    }
  }

  if (!visible || !pertinent) return null;

  return (
    <aside class="notify-banniere" aria-label="Notifications">
      <p class="notify-banniere-texte">
        {installer
          ? "Installe Veillée sur ton écran d'accueil pour recevoir les questions du Conteur : touche Partager ⬆️, puis « Sur l'écran d'accueil ». Ton lien de connexion (onglet Compagnons) t'y fera retrouver ton héros."
          : "Active les notifications pour être prévenu quand le Conteur t'appelle."}
      </p>
      {!installer && (
        <button type="button" class="notify-banniere-action" disabled={occupe} onClick={() => void activer()}>
          Activer
        </button>
      )}
      <button type="button" class="notify-banniere-fermer" aria-label="Refermer le bandeau" onClick={() => setVisible(false)}>
        ×
      </button>
    </aside>
  );
}
